import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { JobStatus } from '@stamp/merch';
import { StatusBadge } from '../components/StatusBadge';
import { ArtworkPreview } from '../components/ArtworkPreview';
import { ProductSelector } from '../components/ProductSelector';
import { SizeSelector } from '../components/SizeSelector';
import { MockupGallery } from '../components/MockupGallery';
import { ReviewGate } from '../components/ReviewGate';
import { PriceInput } from '../components/PriceInput';
import {
  fetchJob,
  type Job,
  type ArtworkCandidate,
  STAGE_ORDER,
} from '../api';

const STAGE_LABELS: Record<string, string> = {
  brief_draft: 'Brief Draft',
  generating: 'Generating Artwork',
  artwork_review: 'Artwork Review',
  artwork_approved: 'Artwork Approved',
  product_configuration: 'Product Configuration',
  mockups_processing: 'Mockup Generation',
  merch_review: 'Final Review',
  ready_to_publish: 'Ready to Publish',
  publishing: 'Publishing',
  published: 'Published',
};

function stageNum(status: JobStatus): number {
  const idx = STAGE_ORDER.indexOf(status);
  return idx >= 0 ? idx : 0;
}

function isLocked(currentStatus: JobStatus, stageStatus: JobStatus): boolean {
  if (currentStatus === 'rejected' || currentStatus === 'archived') {
    return stageNum(stageStatus) > stageNum(currentStatus);
  }
  return stageNum(stageStatus) > stageNum(currentStatus);
}

export function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCandidate, setSelectedCandidate] = useState<string | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [selectedVariants, setSelectedVariants] = useState<number[]>([]);
  const [activeProductId, setActiveProductId] = useState<number | null>(null);
  const [retailPrice, setRetailPrice] = useState(28);
  const [reviewApproved, setReviewApproved] = useState(false);
  const [briefEditOpen, setBriefEditOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    if (!id) return;
    fetchJob(id).then((j) => {
      setJob(j);
      if (j) {
        setSelectedProducts(j.selectedProducts);
        setSelectedCandidate(j.approvedCandidateId ?? null);
        if (j.pricing) setRetailPrice(j.pricing.retail);
        if (j.status === 'published' || j.status === 'ready_to_publish') setReviewApproved(true);
      }
      setLoading(false);
    });
  }, [id]);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="empty-state">
        <div className="empty-state__title">Job not found</div>
        <button className="btn btn--ghost" onClick={() => navigate('/')}>Back to Dashboard</button>
      </div>
    );
  }

  const currentStageNum = stageNum(job.status);

  function advanceStatus(newStatus: JobStatus) {
    if (!job) return;
    setJob({
      ...job,
      status: newStatus,
      updatedAt: new Date().toISOString(),
      timeline: [
        ...job.timeline,
        { status: newStatus, timestamp: new Date().toISOString() },
      ],
    });
  }

  function handleGenerate() {
    advanceStatus('generating');
    // Simulate generation completing
    setTimeout(() => {
      if (!job) return;
      const candidates: ArtworkCandidate[] = Array.from({ length: job.brief.conceptCount }, (_, i) => ({
        id: `c${i + 1}`,
        url: `https://via.placeholder.com/400x400/14110f/c9a96e?text=Concept+${i + 1}`,
        label: `Concept ${i + 1}`,
        seed: Math.floor(Math.random() * 10000),
        approved: false,
      }));
      setJob((prev) => prev ? {
        ...prev,
        status: 'artwork_review',
        candidates,
        updatedAt: new Date().toISOString(),
        timeline: [...prev.timeline, { status: 'artwork_review', timestamp: new Date().toISOString() }],
      } : prev);
    }, 1500);
  }

  function handleApproveArtwork() {
    if (!selectedCandidate) return;
    setJob((prev) => prev ? {
      ...prev,
      status: 'artwork_approved',
      approvedCandidateId: selectedCandidate,
      candidates: prev.candidates.map((c) => ({ ...c, approved: c.id === selectedCandidate })),
      updatedAt: new Date().toISOString(),
      timeline: [...prev.timeline, { status: 'artwork_approved', timestamp: new Date().toISOString(), note: 'Artwork approved' }],
    } : prev);
    setTimeout(() => advanceStatus('product_configuration'), 100);
  }

  function handleRejectArtwork() {
    advanceStatus('generating');
    setTimeout(() => {
      if (!job) return;
      const newCandidates = job.candidates.map((c, i) => ({
        ...c,
        id: `r${i}`,
        url: `https://via.placeholder.com/400x400/14110f/8b1a3a?text=Revision+${i + 1}`,
        seed: Math.floor(Math.random() * 10000),
        approved: false,
      }));
      setJob((prev) => prev ? {
        ...prev,
        status: 'artwork_review',
        candidates: newCandidates,
        selectedProducts: [],
        approvedCandidateId: undefined,
        updatedAt: new Date().toISOString(),
        timeline: [...prev.timeline, { status: 'artwork_review', timestamp: new Date().toISOString(), note: 'Revision requested' }],
      } : prev);
    }, 1500);
  }

  function handleConfirmProducts() {
    advanceStatus('mockups_processing');
    setTimeout(() => {
      setJob((prev) => prev ? {
        ...prev,
        status: 'merch_review',
        mockups: prev.mockups.length > 0 ? prev.mockups : [
          { variant_id: 101, placement: 'front', url: 'https://via.placeholder.com/400x500/14110f/c9a96e?text=Mockup+1', mockup_id: 1 },
          { variant_id: 104, placement: 'front', url: 'https://via.placeholder.com/400x500/14110f/c9a96e?text=Mockup+2', mockup_id: 2 },
          { variant_id: 107, placement: 'front', url: 'https://via.placeholder.com/400x500/8b1a3a/c9a96e?text=Mockup+3', mockup_id: 3 },
        ],
        timeline: [...prev.timeline, { status: 'merch_review', timestamp: new Date().toISOString() }],
      } : prev);
    }, 2000);
  }

  function handleReviewApprove() {
    setReviewApproved(true);
    advanceStatus('ready_to_publish');
  }

  function handleReviewReject(reason: string) {
    advanceStatus('artwork_review');
    setJob((prev) => prev ? {
      ...prev,
      timeline: [...prev.timeline, { status: 'artwork_review', timestamp: new Date().toISOString(), note: `Rejected: ${reason}` }],
    } : prev);
  }

  function handlePublish() {
    setPublishing(true);
    setTimeout(() => {
      advanceStatus('published');
      setPublishing(false);
    }, 2000);
  }

  const approvedArtwork = job.candidates.find((c) => c.id === job.approvedCandidateId);
  const cost = job.pricing?.cost ?? 12;

  return (
    <div>
      {/* Job Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <button className="btn btn--ghost btn--sm" onClick={() => navigate('/')}> &larr; Dashboard</button>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: 'var(--ink-faint)' }}>{job.id}</span>
          </div>
          <h1 style={{ margin: 0 }}>{job.brief.logoPrompt}</h1>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '8px' }}>
            <StatusBadge status={job.status} />
            <span style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}>{job.brand}</span>
          </div>
        </div>
      </div>

      {/* Timeline Sidebar */}
      <div className="panel" style={{ marginBottom: '24px' }}>
        <div className="panel__header">
          <h3 className="panel__title">Workflow Progress</h3>
        </div>
        <div className="timeline">
          {STAGE_ORDER.map((stage, i) => {
            const completed = i < currentStageNum;
            const active = i === currentStageNum && job.status !== 'rejected' && job.status !== 'archived';
            const label = STAGE_LABELS[stage] ?? stage;
            const timelineEntry = job.timeline.find((t) => t.status === stage);
            return (
              <div key={stage} className={`timeline-item ${completed ? 'timeline-item--complete' : ''} ${active ? 'timeline-item--active' : ''}`}>
                <div className="timeline-item__dot">
                  {completed ? '\u2713' : i + 1}
                </div>
                <div className="timeline-item__content">
                  <p className="timeline-item__label">{label}</p>
                  {timelineEntry && (
                    <p className="timeline-item__time">
                      {new Date(timelineEntry.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      {timelineEntry.note && ` - ${timelineEntry.note}`}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Stage 1: Brief */}
      <WorkflowStage
        stageNum={0}
        currentStageNum={currentStageNum}
        status="brief_draft"
        jobStatus={job.status}
        title="Brief"
      >
        {briefEditOpen ? (
          <BriefEditor job={job} onSave={() => setBriefEditOpen(false)} onCancel={() => setBriefEditOpen(false)} />
        ) : (
          <div>
            <BriefDisplay job={job} />
            {job.status === 'brief_draft' && (
              <div style={{ marginTop: '16px', display: 'flex', gap: '10px' }}>
                <button className="btn btn--ghost btn--sm" onClick={() => setBriefEditOpen(true)}>Edit Brief</button>
                <button className="btn btn--primary btn--sm" onClick={handleGenerate}>Generate Artwork</button>
              </div>
            )}
          </div>
        )}
      </WorkflowStage>

      {/* Stage 2: Artwork Generation */}
      <WorkflowStage
        stageNum={1}
        currentStageNum={currentStageNum}
        status="generating"
        jobStatus={job.status}
        title="Artwork Generation"
      >
        {job.status === 'generating' ? (
          <div className="loading">
            <div className="spinner" />
            <p style={{ marginTop: '12px' }}>Generating {job.brief.conceptCount} concepts...</p>
          </div>
        ) : job.candidates.length > 0 ? (
          <div>
            <p style={{ color: 'var(--ink-muted)', marginBottom: '12px' }}>{job.candidates.length} candidates generated. Select one to approve.</p>
            <div className="candidate-grid">
              {job.candidates.map((c) => (
                <div
                  key={c.id}
                  className={`candidate-card ${selectedCandidate === c.id ? 'candidate-card--selected' : ''}`}
                  onClick={() => setSelectedCandidate(c.id)}
                >
                  <img className="candidate-card__img" src={c.url} alt={c.label} />
                  <div className="candidate-card__label">
                    <span>{c.label}</span>
                    {c.approved && <span className="candidate-card__badge">Approved</span>}
                  </div>
                </div>
              ))}
            </div>
            {selectedCandidate && (
              <div style={{ marginTop: '16px' }}>
                <ArtworkPreview src={job.candidates.find((c) => c.id === selectedCandidate)!.url} alt="Selected candidate" />
              </div>
            )}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-state__title">No candidates yet</div>
            <p>Artwork generation will produce candidates here.</p>
          </div>
        )}
      </WorkflowStage>

      {/* Stage 3: Artwork Review */}
      <WorkflowStage
        stageNum={2}
        currentStageNum={currentStageNum}
        status="artwork_review"
        jobStatus={job.status}
        title="Artwork Review"
      >
        {job.candidates.length > 0 ? (
          <div>
            <div className="candidate-grid" style={{ marginBottom: '16px' }}>
              {job.candidates.map((c) => (
                <div
                  key={c.id}
                  className={`candidate-card ${selectedCandidate === c.id ? 'candidate-card--selected' : ''}`}
                  onClick={() => setSelectedCandidate(c.id)}
                >
                  <img className="candidate-card__img" src={c.url} alt={c.label} />
                  <div className="candidate-card__label">
                    <span>{c.label}</span>
                  </div>
                </div>
              ))}
            </div>
            {selectedCandidate && (
              <div style={{ marginBottom: '16px' }}>
                <ArtworkPreview src={job.candidates.find((c) => c.id === selectedCandidate)!.url} alt="Selected for review" />
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                className="btn btn--primary"
                onClick={handleApproveArtwork}
                disabled={!selectedCandidate}
              >
                Approve Selected
              </button>
              <button
                className="btn btn--danger"
                onClick={handleRejectArtwork}
                disabled={!selectedCandidate}
              >
                Request Revision
              </button>
            </div>
          </div>
        ) : (
          <div className="form-hint">No candidates to review yet.</div>
        )}
      </WorkflowStage>

      {/* Stage 4: Product Configuration */}
      <WorkflowStage
        stageNum={4}
        currentStageNum={currentStageNum}
        status="product_configuration"
        jobStatus={job.status}
        title="Product Configuration"
      >
        <div className="form-group">
          <label className="form-label">Select Printful Products</label>
          <ProductSelector selectedIds={selectedProducts} onChange={(ids) => {
            setSelectedProducts(ids);
            setActiveProductId(ids[0] ?? null);
          }} />
        </div>

        {selectedProducts.length > 0 && (
          <div className="form-group">
            <label className="form-label">Select Sizes & Variants</label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              {selectedProducts.map((pid) => (
                <button
                  key={pid}
                  className={`filter-chip ${activeProductId === pid ? 'filter-chip--active' : ''}`}
                  onClick={() => setActiveProductId(pid)}
                >
                  Product {pid}
                </button>
              ))}
            </div>
            <SizeSelector
              productId={activeProductId}
              selectedIds={selectedVariants}
              onChange={setSelectedVariants}
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
          <button
            className="btn btn--primary"
            onClick={handleConfirmProducts}
            disabled={selectedProducts.length === 0}
          >
            Confirm & Generate Mockups
          </button>
        </div>
      </WorkflowStage>

      {/* Stage 5: Mockup Gallery */}
      <WorkflowStage
        stageNum={5}
        currentStageNum={currentStageNum}
        status="mockups_processing"
        jobStatus={job.status}
        title="Mockup Gallery"
      >
        {job.status === 'mockups_processing' ? (
          <MockupGallery mockups={[]} loading />
        ) : job.status === 'product_configuration' ? (
          <div className="form-hint">Mockups will be generated after product configuration.</div>
        ) : (
          <MockupGallery mockups={job.mockups} />
        )}
      </WorkflowStage>

      {/* Stage 6: Final Review */}
      <WorkflowStage
        stageNum={6}
        currentStageNum={currentStageNum}
        status="merch_review"
        jobStatus={job.status}
        title="Final Review"
      >
        {approvedArtwork && (
          <div style={{ marginBottom: '16px' }}>
            <ArtworkPreview src={approvedArtwork.url} alt="Approved artwork" />
          </div>
        )}
        <div className="form-row" style={{ marginBottom: '16px' }}>
          <PriceInput
            value={retailPrice}
            onChange={setRetailPrice}
            cost={cost}
            label="Retail Price"
          />
          <div className="form-group">
            <label className="form-label">Product Name</label>
            <input
              className="form-input"
              defaultValue={job.brief.logoPrompt.split(' ').slice(0, 4).join(' ')}
            />
          </div>
        </div>
        <ReviewGate
          approved={reviewApproved}
          onApprove={handleReviewApprove}
          onReject={handleReviewReject}
        />
      </WorkflowStage>

      {/* Stage 7: Publishing */}
      <WorkflowStage
        stageNum={9}
        currentStageNum={currentStageNum}
        status="ready_to_publish"
        jobStatus={job.status}
        title="Publishing"
      >
        {job.status === 'published' ? (
          <div className="panel" style={{ borderColor: 'var(--green)', textAlign: 'center' }}>
            <div style={{ fontSize: '1.2rem', color: 'var(--green)', fontWeight: 600, marginBottom: '8px' }}>
              Published Successfully
            </div>
            <p style={{ color: 'var(--ink-muted)' }}>
              Products are live in the store. {selectedProducts.length} product(s) published with {selectedVariants.length} variant(s).
            </p>
          </div>
        ) : job.status === 'ready_to_publish' ? (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <p style={{ marginBottom: '16px', color: 'var(--ink-muted)' }}>
              Everything is approved. Ready to publish to the Printful store.
            </p>
            <button className="btn btn--primary btn--lg" onClick={handlePublish} disabled={publishing}>
              {publishing ? 'Publishing...' : 'Publish to Store'}
            </button>
          </div>
        ) : job.status === 'publishing' ? (
          <div className="loading">
            <div className="spinner" />
            <p style={{ marginTop: '12px' }}>Publishing to store...</p>
          </div>
        ) : (
          <div className="form-hint">Publishing will be available after review approval.</div>
        )}
      </WorkflowStage>

      {/* Rejected / Archived banner */}
      {(job.status === 'rejected' || job.status === 'archived') && (
        <div className="panel" style={{ borderColor: job.status === 'rejected' ? 'var(--red)' : 'var(--border-light)' }}>
          <h3 className="panel__title" style={{ color: job.status === 'rejected' ? 'var(--red)' : 'var(--ink-faint)' }}>
            {job.status === 'rejected' ? 'Job Rejected' : 'Job Archived'}
          </h3>
          <p style={{ color: 'var(--ink-muted)' }}>
            {job.timeline.find((t) => t.status === job.status)?.note ?? 'No additional notes.'}
          </p>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* WorkflowStage component                                           */
/* ---------------------------------------------------------------- */

interface WorkflowStageProps {
  stageNum: number;
  currentStageNum: number;
  status: JobStatus;
  jobStatus: JobStatus;
  title: string;
  children: React.ReactNode;
}

function WorkflowStage({ stageNum, currentStageNum, status, jobStatus, title, children }: WorkflowStageProps) {
  const locked = isLocked(jobStatus, status);
  const active = jobStatus === status;
  const complete = stageNum < currentStageNum;

  const stageClass = locked ? 'workflow-stage--locked' : active ? 'workflow-stage--active' : complete ? 'workflow-stage--complete' : '';

  return (
    <div className={`workflow-stage ${stageClass}`}>
      <div className="workflow-stage__header">
        <div className="workflow-stage__title">
          <span className={`workflow-stage__index ${active ? '' : ''}`}>{complete ? '\u2713' : stageNum + 1}</span>
          {title}
        </div>
        {active && <StatusBadge status={status} size="sm" />}
        {locked && <span style={{ fontSize: '0.72rem', color: 'var(--ink-faint)' }}>Locked</span>}
      </div>
      <div className="workflow-stage__body">
        {locked ? (
          <div className="lock-overlay">Complete previous stages to unlock.</div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Brief display & editor                                           */
/* ---------------------------------------------------------------- */

function BriefDisplay({ job }: { job: Job }) {
  const rows: { label: string; value: string }[] = [
    { label: 'Brand', value: job.brief.brand },
    { label: 'Logo Prompt', value: job.brief.logoPrompt },
    { label: 'Required Text', value: job.brief.requiredText ?? '-' },
    { label: 'Style', value: job.brief.style ?? '-' },
    { label: 'Colors', value: job.brief.colors?.join(', ') ?? '-' },
    { label: 'Exclusions', value: job.brief.exclusions?.join(', ') ?? '-' },
    { label: 'Intended Use', value: job.brief.intendedUse.replace(/_/g, ' ') },
    { label: 'Garment Types', value: job.brief.garmentTypes?.join(', ') ?? '-' },
    { label: 'Garment Colors', value: job.brief.garmentColors?.join(', ') ?? '-' },
    { label: 'Concepts', value: String(job.brief.conceptCount) },
  ];

  return (
    <div className="table-wrap">
      <table className="data-table">
        <tbody>
          {rows.map((r) => (
            <tr key={r.label} style={{ cursor: 'default' }}>
              <td style={{ width: '160px', color: 'var(--ink-faint)', fontWeight: 500, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{r.label}</td>
              <td style={{ color: 'var(--ink)' }}>{r.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BriefEditor({ job, onSave, onCancel }: { job: Job; onSave: () => void; onCancel: () => void }) {
  const [prompt, setPrompt] = useState(job.brief.logoPrompt);
  const [requiredText, setRequiredText] = useState(job.brief.requiredText ?? '');
  const [style, setStyle] = useState(job.brief.style ?? '');

  return (
    <div>
      <div className="form-group">
        <label className="form-label">Logo Prompt</label>
        <textarea className="form-textarea" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Required Text</label>
          <input className="form-input" value={requiredText} onChange={(e) => setRequiredText(e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Style</label>
          <input className="form-input" value={style} onChange={(e) => setStyle(e.target.value)} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '10px' }}>
        <button className="btn btn--primary btn--sm" onClick={onSave}>Save</button>
        <button className="btn btn--ghost btn--sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
